import moment from 'moment'

import { reportingColors } from '../../../utils/colors'
import * as DateUtils from '../../../utils/dates'
import { currencyFormatReporting } from '../../../utils/numbers'
import {
    BoundedRect,
    PSVisualizationDataType,
    Point,
    drawRoundedRect,
} from '../../../utils/visualization'
import {
    DEFAULT_CAPTION_FONT_SIZE,
    DEFAULT_LABEL_FONT_SIZE,
    DEFAULT_LIGHT_FONT,
} from '../../../utils/visualization'
import { BarElement, BarElementState, DEFAULT_BAR_SPACING } from './BarElement'

const BOTTOM_LABEL_MARGIN = 12
const LEFT_MARGIN = 0
const CHART_INNER_PADDING = 20
const DEFAULT_BAR_WIDTH = 120
const TOP_LABEL_MARGIN = 6
const RIGHT_LABEL_MARGIN = 12

const TICK_HEIGHT = 8
const TOP_CAPTION_HEIGHT = 35
const TOP_LABEL_PADDING_HORIZ = 12
const TOP_LABEL_PADDING_VERTICAL = 8

export enum BarChartBinType {
    YEAR,
    QUARTER,
    MONTH,
    WEEK,
    DAY,
}

enum LabelAnchorType {
    Left = 'LEFT',
    Top = 'TOP',
}

export const generateBarChartRenderer = () => {
    const tickHeight = TICK_HEIGHT
    const dateLocale = DateUtils.DEFAULT_DATE_LOCALE
    const labelFontName = DEFAULT_LIGHT_FONT
    const labelFontSize = DEFAULT_LABEL_FONT_SIZE
    const captionFontSize = DEFAULT_CAPTION_FONT_SIZE
    const secondBottomLabelMargin = 2

    const barRepository: BarElement[] = []
    const chartRect: BoundedRect = { left: 0, right: 0, top: 0, bottom: 0 }

    var values: number[] = []
    var labels: moment.Moment[] = []
    var fromDate: moment.Moment | null = null
    var toDate: moment.Moment | null = null
    var dataType: PSVisualizationDataType = PSVisualizationDataType.CURRENCY
    var barColor: string = reportingColors.blue400
    var bin: BarChartBinType = BarChartBinType.MONTH
    var dpr: number = 1

    const drawValues = (
        canvas: HTMLCanvasElement,
        valuesArg: number[],
        labelsArg: moment.Moment[],
        binArg: BarChartBinType,
        fromDateArg: moment.Moment | null,
        toDateArg: moment.Moment | null,
        colorArg: string,
        dataTypeArg: PSVisualizationDataType,
        selectedBarIndexArg: number | null,
        dprArg: number
    ) => {
        values = valuesArg
        labels = labelsArg
        bin = binArg
        fromDate = fromDateArg
        toDate = toDateArg
        barColor = colorArg
        dataType = dataTypeArg
        dpr = dprArg
        draw(canvas, selectedBarIndexArg)
    }

    const drawCircle = (
        ctx: CanvasRenderingContext2D,
        x: number,
        y: number,
        radius: number,
        fillColor: string,
        borderColor: string,
        borderWidth: number
    ) => {
        ctx.globalCompositeOperation = 'source-over' // Reset the global composite operation
        ctx.beginPath()
        ctx.arc(x, y, radius, 0, 2 * Math.PI, false)
        ctx.fillStyle = fillColor
        ctx.fill()
        ctx.lineWidth = borderWidth
        ctx.strokeStyle = borderColor
        ctx.stroke()
    }

    const displayLabel = (
        ctx: CanvasRenderingContext2D,
        labelText: string,
        position: Point,
        anchorType: LabelAnchorType
    ) => {
        let x = position.x
        let y = position.y
        if (anchorType === LabelAnchorType.Left) {
            ctx.textAlign = 'left'
            ctx.textBaseline = 'middle'
        }
        else if (anchorType === LabelAnchorType.Top) {
            ctx.textAlign = 'center'
            ctx.textBaseline = 'top'
        }
        ctx.fillStyle = reportingColors.gray600
        ctx.font = `${labelFontSize}px ${labelFontName}`
        ctx.fillText(labelText, x, y)
    }

    const showCaptionForBar = (canvas: HTMLCanvasElement, bar: BarElement, y: number) => {
        const ctx = canvas.getContext('2d')
        if (ctx === null) {
            return
        }

        const circleRadius = 3
        drawCircle(ctx, bar.centerHorizontal, bar.origin.y, circleRadius, 'white', barColor, 2)
        drawLine(
            ctx,
            { x: bar.centerHorizontal, y: bar.origin.y - circleRadius },
            { x: bar.centerHorizontal, y: y },
            'black',
            2,
            [3, 3]
        )
        displayBarValueAndDates(canvas, ctx, bar, y, 200)
    }

    const labelStringFromValue = (value: number) => {
        let unit = ''
        if (value >= 1000000) {
            value /= 1000000
            unit = 'M'
        }
        else if (value >= 1000) {
            value /= 1000
            unit = 'K'
        }
        value = Math.floor(value * 10) / 10
        let str = value.toFixed(1)
        if (Math.floor(value) === value) {
            str = str.split('.')[0]
        }
        return str + unit
    }

    const labelStringForDate = (date: Date, bin: BarChartBinType): string => {
        let str = ''
        if (bin === BarChartBinType.DAY) {
            str = date.toLocaleDateString(dateLocale, { month: 'short', day: 'numeric' })
        }
        else if (bin === BarChartBinType.WEEK) {
            str = date.toLocaleDateString(dateLocale, { month: 'short', day: 'numeric' })
        }
        else if (bin === BarChartBinType.MONTH) {
            str = date.toLocaleDateString(dateLocale, { month: 'short' })
        }
        else if (bin === BarChartBinType.QUARTER) {
            str = 'Q' + (Math.floor(date.getMonth() / 3) + 1).toString()
        }
        else if (bin === BarChartBinType.YEAR) {
            str = date.toLocaleDateString(dateLocale, { year: 'numeric' })
        }
        return str
    }

    const captionStringForDate = (date: Date, fromDate: Date | null, toDate: Date | null) => {
        let str = ''
        if (bin === BarChartBinType.DAY) {
            const prefix = 'on '
            str =
                prefix +
                date.toLocaleDateString(dateLocale, {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                })
        }
        else if (bin === BarChartBinType.WEEK) {
            const prefix = 'from '
            let startDate = fromDate || DateUtils.getFirstDayOfWeek(date)
            let endDate = toDate || DateUtils.getLastDayOfWeek(date)

            if (DateUtils.isSameMonthAndYear(startDate, endDate)) {
                str =
                    prefix +
                    startDate.toLocaleDateString(dateLocale, { month: 'short', day: 'numeric' }) +
                    ' - ' +
                    endDate.toLocaleDateString(dateLocale, { day: 'numeric' })
            }
            else if (DateUtils.isSameYear(startDate, endDate)) {
                str =
                    prefix +
                    startDate.toLocaleDateString(dateLocale, { month: 'short', day: 'numeric' }) +
                    ' - ' +
                    endDate.toLocaleDateString(dateLocale, { month: 'short', day: 'numeric' })
            }
            else {
                const dateFormat = { year: 'numeric', month: 'short', day: 'numeric' }
                str =
                    prefix +
                    startDate.toLocaleDateString(dateLocale, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                    }) +
                    ' - ' +
                    endDate.toLocaleDateString(dateLocale, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                    })
            }
        }
        else if (bin === BarChartBinType.MONTH) {
            let startDate = fromDate || DateUtils.getFirstDayOfMonth(date)
            let endDate = toDate || DateUtils.getLastDayOfMonth(date)
            if (DateUtils.isFirstDayOfMonth(startDate) && DateUtils.isLastDayOfMonth(endDate)) {
                str = 'in ' + startDate.toLocaleDateString(dateLocale, { month: 'long' })
            }
            else if (DateUtils.isSameMonthAndYear(startDate, endDate)) {
                str =
                    'from ' +
                    startDate.toLocaleDateString(dateLocale, { month: 'short', day: 'numeric' }) +
                    ' - ' +
                    endDate.toLocaleDateString(dateLocale, { day: 'numeric' })
            }
            else if (DateUtils.isSameYear(startDate, endDate)) {
                str =
                    'from ' +
                    startDate.toLocaleDateString(dateLocale, { month: 'short', day: 'numeric' }) +
                    ' - ' +
                    endDate.toLocaleDateString(dateLocale, { month: 'short', day: 'numeric' })
            }
            else {
                str =
                    'from ' +
                    startDate.toLocaleDateString(dateLocale, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                    }) +
                    ' - ' +
                    endDate.toLocaleDateString(dateLocale, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                    })
            }
        }
        else if (bin === BarChartBinType.QUARTER) {
            let prefix = 'from '
            let startDate = fromDate || DateUtils.getFirstDayOfQuarter(date)
            let endDate = toDate || DateUtils.getLastDayOfQuarter(date)
            if (DateUtils.isFirstDayOfMonth(startDate) && DateUtils.isLastDayOfMonth(endDate)) {
                str =
                    prefix +
                    startDate.toLocaleDateString(dateLocale, { month: 'long' }) +
                    ' - ' +
                    endDate.toLocaleDateString(dateLocale, { month: 'long' })
            }
            else if (DateUtils.isSameMonthAndYear(startDate, endDate)) {
                str =
                    prefix +
                    startDate.toLocaleDateString(dateLocale, { month: 'short', day: 'numeric' }) +
                    ' - ' +
                    endDate.toLocaleDateString(dateLocale, { day: 'numeric' })
            }
            else {
                str =
                    prefix +
                    startDate.toLocaleDateString(dateLocale, { month: 'short', day: 'numeric' }) +
                    ' - ' +
                    endDate.toLocaleDateString(dateLocale, { month: 'short', day: 'numeric' })
            }
        }
        else if (bin === BarChartBinType.YEAR) {
            let startDate = fromDate || DateUtils.getFirstDayOfYear(date)
            let endDate = toDate || DateUtils.getLastDayOfYear(date)
            if (DateUtils.isFirstDayOfMonth(startDate) && DateUtils.isLastDayOfMonth(endDate)) {
                str = 'in ' + startDate.toLocaleDateString(dateLocale, { year: 'numeric' })
            }
            else {
                str =
                    'from ' +
                    startDate.toLocaleDateString(dateLocale, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                    }) +
                    ' - ' +
                    endDate.toLocaleDateString(dateLocale, { month: 'short', day: 'numeric' })
            }
        }
        return str
    }

    const drawLine = (
        ctx: CanvasRenderingContext2D,
        p0: Point,
        p1: Point,
        color: string,
        lineWidth: number,
        dashPattern?: number[]
    ) => {
        ctx.beginPath()
        ctx.moveTo(p0.x, p0.y)
        ctx.lineTo(p1.x, p1.y)
        ctx.strokeStyle = color
        ctx.lineWidth = lineWidth
        if (dashPattern != null) {
            ctx.setLineDash(dashPattern)
        }
        else {
            ctx.setLineDash([])
        }
        ctx.stroke()
    }

    const displayBarValueAndDates = (
        canvas: HTMLCanvasElement,
        ctx: CanvasRenderingContext2D,
        bar: BarElement,
        y: number,
        maxWidth: number
    ) => {
        const fontStyle = `${captionFontSize}px ${labelFontName}`
        const fontStyleBold = `bold ${fontStyle}`

        let formattedValue = currencyFormatReporting(bar.getValue())
        if (dataType === PSVisualizationDataType.INTEGER) {
            formattedValue = `${Math.floor(bar.getValue())}`
        }
        else if (dataType === PSVisualizationDataType.PERCENT) {
            formattedValue = `${Math.floor(bar.getValue())}%`
        }

        const valueString = formattedValue
        ctx.font = fontStyleBold
        const valueStringSize = ctx.measureText(valueString)

        const datesString =
            ' ' + captionStringForDate(bar.getLabelDate()!, bar.fromDate, bar.toDate)
        ctx.font = fontStyle
        const datesStringSize = ctx.measureText(datesString)

        const textWidth = valueStringSize.width + datesStringSize.width
        const valueTextHeight =
            valueStringSize.actualBoundingBoxAscent + valueStringSize.actualBoundingBoxDescent
        const datesTextHeight =
            datesStringSize.actualBoundingBoxAscent + datesStringSize.actualBoundingBoxDescent
        const textHeight = Math.max(valueTextHeight, datesTextHeight)

        let x = bar.centerHorizontal - textWidth / 2 - TOP_LABEL_PADDING_HORIZ
        const captionWidth = textWidth + 2 * TOP_LABEL_PADDING_HORIZ
        if (x + captionWidth > canvas.width / dpr) {
            x = canvas.width / dpr - captionWidth
        }
        else if (x < 0) {
            x = 0
        }
        y -= textHeight

        drawRoundedRect(
            ctx,
            x,
            y - TOP_LABEL_PADDING_VERTICAL,
            captionWidth,
            textHeight + 2 * TOP_LABEL_PADDING_VERTICAL,
            TOP_LABEL_PADDING_HORIZ,
            reportingColors.gray200
        )
        ctx.fillStyle = 'black'
        ctx.font = fontStyleBold
        x += TOP_LABEL_PADDING_HORIZ
        ctx.textAlign = 'left'
        ctx.textBaseline = 'top'
        ctx.fillStyle = reportingColors.gray700
        ctx.fillText(valueString, x, y, maxWidth)
        ctx.font = fontStyle
        x += valueStringSize.width
        ctx.fillStyle = reportingColors.gray600
        ctx.fillText(datesString, x, y, maxWidth)
    }

    const draw = (canvas: HTMLCanvasElement, selectedBarIndex: number | null) => {
        const ctx = canvas.getContext('2d')
        if (ctx === null) {
            return
        }

        const ww = canvas.width / dpr
        const hh = canvas.height / dpr

        barRepository.forEach((bar) => {
            bar.reset()
        })
        ctx.clearRect(0, 0, canvas.width, canvas.height)

        var labelText = '00.0K'
        ctx.font = `${labelFontSize}px ${labelFontName}`
        const labelSize = ctx.measureText(labelText)
        const labelHeight = labelSize.actualBoundingBoxAscent + labelSize.actualBoundingBoxDescent

        const h1 = TOP_CAPTION_HEIGHT
        const h2 = h1 + TOP_LABEL_MARGIN
        const h3 = h2 + labelHeight + TOP_LABEL_MARGIN
        var h5 = hh - labelHeight
        var h4 = h5 - (BOTTOM_LABEL_MARGIN + tickHeight)
        var h6 = hh

        var v1 = LEFT_MARGIN
        var v3 = ww - RIGHT_LABEL_MARGIN
        var v2 = v3 - RIGHT_LABEL_MARGIN

        const axisLinesWidth = 2

        let hasSecondLabel = false
        let hasMultipleYears = false
        if (labels.length > 0) {
            hasMultipleYears = labels[0].year !== labels[labels.length - 1].year
            const startsThisYear = labels[0].year() === moment().year()
            hasSecondLabel = (hasMultipleYears || !startsThisYear) && bin !== BarChartBinType.YEAR
            if (hasSecondLabel) {
                h6 -= labelHeight
                h4 -= labelHeight + secondBottomLabelMargin
                h5 -= labelHeight + secondBottomLabelMargin
            }
        }

        displayLabel(ctx, '0', { x: v3, y: h4 }, LabelAnchorType.Left)
        drawLine(ctx, { x: v1, y: h4 }, { x: v2, y: h4 }, reportingColors.gray400, axisLinesWidth)

        if (values != null && values.length > 0) {
            //adjust barRepository length if values array length changed
            if (values.length > barRepository.length) {
                for (let i = barRepository.length; i < values.length; i++) {
                    barRepository.push(new BarElement())
                }
            }
            else if (values.length < barRepository.length) {
                barRepository.splice(values.length)
            }

            var maxValue: number = Math.max(...values)

            if (dataType === PSVisualizationDataType.PERCENT) {
                maxValue = Math.max(maxValue, 100)
            }

            const halfLabelSize = ctx.measureText((maxValue / 2).toString())
            const maxLabelSize = ctx.measureText(maxValue.toString())

            v3 = ww - Math.max(maxLabelSize.width, halfLabelSize.width)
            v2 = v3 - RIGHT_LABEL_MARGIN

            if (maxValue > 0) {
                // draw horizontal labels and dashed lines
                const maxLabelString = labelStringFromValue(maxValue)
                const halfLabelString = labelStringFromValue(maxValue / 2)
                displayLabel(ctx, maxLabelString, { x: v3, y: h3 }, LabelAnchorType.Left)
                displayLabel(
                    ctx,
                    halfLabelString,
                    { x: v3, y: (h3 + h4) / 2 },
                    LabelAnchorType.Left
                )

                drawLine(
                    ctx,
                    { x: v1, y: h3 },
                    { x: v2, y: h3 },
                    reportingColors.gray300,
                    1,
                    [4, 4]
                )
                drawLine(
                    ctx,
                    { x: v1, y: (h3 + h4) / 2 },
                    { x: v2, y: (h3 + h4) / 2 },
                    reportingColors.gray300,
                    1,
                    [4, 4]
                )
            }
            else {
                maxValue = 1
            }

            const chartHasPadding = values.length < 14
            const centered = values.length < 4
            var tickInterval = Math.ceil(values.length / 7)

            const firstXLabel = labelStringForDate(labels[0].toDate(), bin)
            const firstXLabelWidth = ctx.measureText(firstXLabel).width
            const lastXLabel = labelStringForDate(labels[labels.length - 1].toDate(), bin)
            const lastXLabelWidth = ctx.measureText(lastXLabel).width

            const viewPortWidth = v2 - v1

            let x0 = 0
            let x1 = viewPortWidth

            let barWidth = DEFAULT_BAR_WIDTH
            let barSpacing = DEFAULT_BAR_SPACING

            if (centered) {
                const barsWidth = (values.length - 1) * (barWidth + barSpacing) + barWidth
                x0 = (viewPortWidth - barsWidth) / 2
            }
            else {
                if (chartHasPadding) {
                    x0 += CHART_INNER_PADDING
                    x1 -= CHART_INNER_PADDING
                }
                let barsWidth = x1 - x0 - (values.length - 1) * barSpacing
                barWidth = barsWidth / labels.length

                if (firstXLabelWidth / 2 > x0 + barWidth / 2) {
                    x0 += firstXLabelWidth / 2
                }
                if (lastXLabelWidth / 2 > barWidth / 2 + (ww - x1)) {
                    x1 -= lastXLabelWidth / 2
                }
                // recalculate bar width
                barsWidth = x1 - x0 - (values.length - 1) * barSpacing
                barWidth = barsWidth / labels.length
            }

            chartRect.left = x0
            chartRect.right = x1
            chartRect.top = h3
            chartRect.bottom = h4

            let labelHasYear = hasSecondLabel && !hasMultipleYears
            let barCount = 0
            let maxBarHeight = h4 - h3

            // Draw bars
            barRepository.forEach((bar, index) => {
                var barState = BarElementState.NEUTRAL
                if (selectedBarIndex !== null) {
                    barState =
                        selectedBarIndex === index
                            ? BarElementState.SELECTED
                            : BarElementState.UNSELECTED
                }
                bar.setValues({
                    origin: { x: x0, y: h3 - axisLinesWidth / 2 },
                    width: barWidth,
                    maxBarHeight: maxBarHeight,
                    value: values[index],
                    maxValue: maxValue,
                    label: labels[index],
                    color: barColor,
                    state: barState,
                })
                bar.draw(ctx)

                if (index % tickInterval === 0) {
                    const label = labels[index]
                    if (index > 0) {
                        labelHasYear =
                            hasSecondLabel && labels[index].year !== labels[index - 1].year
                    }
                    // draw tick
                    var tickX = bar.centerHorizontal
                    drawLine(
                        ctx,
                        { x: tickX, y: h4 },
                        { x: tickX, y: h4 + tickHeight },
                        reportingColors.gray400,
                        2
                    )
                    // draw label
                    const labelString = labelStringForDate(label.toDate(), bin)
                    displayLabel(
                        ctx,
                        labelString,
                        { x: bar.centerHorizontal, y: h4 + BOTTOM_LABEL_MARGIN },
                        LabelAnchorType.Top
                    )
                    if (labelHasYear) {
                        const labelString = labelStringForDate(label.toDate(), BarChartBinType.YEAR)
                        displayLabel(
                            ctx,
                            labelString,
                            { x: x0 + v1, y: h5 + secondBottomLabelMargin },
                            LabelAnchorType.Top
                        )
                    }
                }
                if (selectedBarIndex === index) showCaptionForBar(canvas, bar, h1)
                barCount += 1
                x0 += barWidth + barSpacing
            })

            if (hasSecondLabel && hasMultipleYears) {
                const label = fromDate != null ? fromDate : labels[0]
                const labelString = labelStringForDate(label.toDate(), BarChartBinType.YEAR)
                displayLabel(
                    ctx,
                    labelString,
                    { x: barRepository[0].centerHorizontal, y: h6 },
                    LabelAnchorType.Top
                )
            }
        }
    }

    return { drawValues, barRepository, chartRect }
}
